import React from 'react';
import { Download, BookOpen, ShieldCheck, FileText, Cookie, Smartphone, Monitor, ExternalLink, Heart } from 'lucide-react';
import { CkLogo } from './CkLogo';

interface FooterProps {
  onOpenDownloads: () => void;
  onOpenGuide: (tab?: 'android' | 'windows' | 'security') => void;
  onOpenLegal: (doc: 'privacy' | 'terms' | 'cookies') => void;
}

export const Footer: React.FC<FooterProps> = ({
  onOpenDownloads,
  onOpenGuide,
  onOpenLegal
}) => {
  return (
    <footer className="w-full border-t border-white/5 bg-[#0a0c14]/80 backdrop-blur-md mt-12 py-8 px-4">
      <div className="max-w-7xl mx-auto flex flex-col gap-6">
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Brand & Tagline */}
          <div className="flex items-center gap-3">
            <CkLogo size={32} />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-white tracking-wide text-sm">CK Share</span>
                <span className="text-[10px] font-semibold text-cyan-400 bg-cyan-500/10 border border-cyan-500/30 px-1.5 py-0.2 rounded-full">
                  v1.2.0
                </span>
              </div>
              <p className="text-[11px] text-gray-500">Universal End-to-End Encrypted Clipboard Sync</p>
            </div>
          </div>

          {/* Quick Navigation Links */}
          <div className="flex items-center flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-gray-400">
            <button
              onClick={onOpenDownloads}
              className="flex items-center gap-1.5 hover:text-cyan-300 transition-colors font-medium text-cyan-400"
            >
              <Download size={13} />
              <span>Download Apps</span>
            </button>

            <button
              onClick={() => onOpenGuide('android')}
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Smartphone size={13} className="text-emerald-400" />
              <span>Android Guide</span>
            </button>

            <button
              onClick={() => onOpenGuide('windows')}
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Monitor size={13} className="text-purple-400" />
              <span>Windows Guide</span>
            </button>

            <button
              onClick={() => onOpenGuide('security')}
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <ShieldCheck size={13} className="text-cyan-400" />
              <span>Security Whitepaper</span>
            </button>

            <span className="text-gray-700 hidden sm:inline">|</span>

            <button
              onClick={() => onOpenLegal('privacy')}
              className="hover:text-white transition-colors"
            >
              Privacy Policy
            </button>

            <button
              onClick={() => onOpenLegal('terms')}
              className="hover:text-white transition-colors"
            >
              Terms of Service
            </button>

            <button
              onClick={() => onOpenLegal('cookies')}
              className="flex items-center gap-1 hover:text-white transition-colors"
            >
              <Cookie size={12} className="text-amber-400" />
              <span>Cookies</span>
            </button>
          </div>

          {/* Live Status Badge */}
          <div className="flex items-center gap-2 text-[11px] text-gray-400 bg-white/[0.03] border border-white/5 px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Cloudflare Edge Relay • Zero Knowledge</span>
          </div>
        </div>

        {/* Creator Attribution Section */}
        <div className="w-full pt-5 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <div className="flex items-center gap-1.5">
            <span>Made with ⚡ by</span>
            <a
              href="http://chintank.42web.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 hover:from-cyan-300 hover:to-purple-300 font-bold transition-all inline-flex items-center gap-1 drop-shadow-[0_0_8px_rgba(0,240,255,0.3)]"
            >
              <span>Chintan Kheradiya</span>
              <ExternalLink size={12} className="text-cyan-400" />
            </a>
          </div>

          <p className="font-mono text-[11px] text-gray-500">
            Enterprise-Grade AES-GCM-256 + PBKDF2 (100k) • 100% Free &amp; Open Source
          </p>
        </div>

      </div>
    </footer>
  );
};
